import {
  CancellationType,
  IAddressInput,
  IBasicOptionInput,
  ICreditCardInput,
  IPolicyHoldersInput,
  IQuotePriceSummary,
  IRegulatoryDeclarationInput,
  ISpecifiedItemInput,
  IValidation,
  QuoteStage,
  QuoteType,
} from '@nibtravel/shared/util/models';

import { IQuoteInput, IQuoteParams } from '../models';

export namespace QuoteActions {
  /** Action to retrieve quote object using API */
  export class FindQuote {
    public static readonly type = '[Quote] Find Quote';

    constructor(
      public brand: string,
      public quoteId: string,
      public planId?: number
    ) {}
  }

  export class NavigateToQuote {
    public static readonly type = '[Quote] Navigate to Quote';
    constructor(public toKosmos?: boolean) {}
  }

  /** Action to update quote object using API */
  export class GetQuote {
    public static readonly type = '[Quote] Get Quote';
  }

  /** Action to submit a quote  */
  export class SubmitQuote {
    public static readonly type = '[Quote] Submit Quote';
    constructor(public data: { errors?: any }) {}
  }

  export class ApplyPromoCode {
    public static readonly type = '[Quote] Apply PromoCode';

    constructor(public campaignCode: string) {}
  }

  export class ResetPromoCode {
    public static readonly type = '[Quote] Reset PromoCode';
  }

  export class UpdateQuote {
    public static readonly type = '[Quote] Update Quote';
    constructor(public data: { errors?: any }) {}
  }

  /** Action to set the policyholder age from traveller details */
  export class UpdateAge {
    public static readonly type = '[Traveller Details] Update Age';

    constructor(public id: string, public age: number) {}
  }

  /** Action to reset quote input in the sidebar */
  export class ResetToOriginalQuoteInput {
    public static readonly type = '[Quote] Reset To Original Quote Input';

    constructor(public data: any) {}
  }

  /** Action to update policyholder with ambiguity and traveller type */
  export class UpdatePolicyholder {
    public static readonly type =
      '[Quote] Update policyholder with ambguity and type';
  }

  /** Action to update ambiguity and dependant flags of policyholder */
  export class UpdateAmbiguity {
    public static readonly type = '[Quote] update policyholder ambiguity';

    constructor(public isDependent: string, public guid: string) {}
  }

  /**
   * Action to define a plan for the current quote.
   * This action is triggered when user choses a plan
   */
  export class SetPlanForQuote {
    public static readonly type = '[Quote] User selected plan';

    constructor(public planId: number | undefined) {}
  }

  /** Action to Purchase Quote using API */
  export class Purchase {
    public static readonly type = '[Quote] Purchase quote';

    constructor(public payload: ICreditCardInput) {}
  }

  /**
   * Action to Verify user input for verification of Purchase using API
   * Used with 3ds payment flow
   */
  export class VerifyPurchase {
    public static readonly type = '[Quote] Verify Purchase';

    constructor(public response: string) {}
  }

  /** Action to reset form */
  export class ResetForm {
    public static readonly type = '[Quote] Reset Form';

    constructor(public payload: Partial<IQuoteInput>) {}
  }

  /** Action used by the quote directive to sync form data to state */
  export class UpdateFormValue {
    public static readonly type = '[Quote] Update Form value';

    constructor(public value: any, public model: string) {}
  }

  /** Action used by the quote directive to sync form dirty to state */
  export class UpdateFormDirty {
    public static readonly type = '[Quote] Update Form dirty';

    constructor(
      public payload: {
        dirty: boolean | null;
        path: string;
      }
    ) {}
  }

  /** Action used by the quote directive to sync form status to state */
  export class UpdateFormStatus {
    public static readonly type = '[Quote] Update Form status';

    constructor(
      public payload: {
        status: string | null;
        path: string;
      }
    ) {}
  }

  /** Action used by the quote directive to sync form errors to state */
  export class UpdateFormErrors {
    public static readonly type = '[Quote] Update Form errors';

    constructor(
      public payload: {
        errors: Record<string, string> | null;
        path: string;
      }
    ) {}
  }

  /** Action to set the current quote stage on state */
  export class SetQuoteStage {
    public static readonly type = '[Quote] Set quote stage';

    constructor(public stage: QuoteStage) {}
  }

  /** Action to set the current route. The purpose is similar to the quote stage */
  export class SetQuoteCurrentRoute {
    public static readonly type = '[Quote] Set quote current route';

    constructor(public route: string) {}
  }

  /** Action to set the quote type to the current quote */
  export class SetQuoteType {
    public static readonly type = '[Quote] Set quote type';

    constructor(public quoteType: QuoteType) {}
  }

  /** Action to set a basic option */
  export class SetBasicOptions {
    public static readonly type = '[Quote] Set basic option';

    constructor(public optionInput: IBasicOptionInput) {}
  }

  export class SetTripCancellationOption {
    public static readonly type = '[Quote] Set trip cancellation option';

    constructor(public optionType: CancellationType, public value: number) {}
  }

  export class SetQuoteParams {
    public static readonly type = '[Quote] Set quote params';

    constructor(public quoteParams: IQuoteParams) {}
  }

  export class SetQuoteParamsToKosmos {
    public static readonly type = '[Quote] Set quote params to Kosmos';

    constructor(public quoteParams: IQuoteParams) {}
  }

  /**
   * Action to set a specified item option.
   * The action handles add/update
   */
  export class SetSpecifiedItem {
    public static readonly type = '[Quote] Set specified item';

    constructor(public itemInput: ISpecifiedItemInput) {}
  }

  export class SetHomeCountry {
    public static readonly type = '[Quote] Set home country';

    constructor(public countryCode: string) {}
  }

  /** Action to delete a specified item option */
  export class DeleteSpecifiedItem {
    public static readonly type = '[Quote] Delete specified item';

    constructor(public itemInput: ISpecifiedItemInput) {}
  }

  /** Action to set membership number */
  export class SetMembership {
    public static readonly type = '[Quote] Set membership number';

    constructor(public membershipNumber: string) {}
  }

  /** Reset quote fields that might create an issue when coming back to QQC */
  export class QqcReset {
    public static readonly type = '[Quote] Qqc Reset';
  }

  /** After purchase, if the user comes back to home page and try to purchase, the whole quote needs to be reset */
  export class ResetQuote {
    public static readonly type = '[Quote] Reset quote';
  }

  /**
   * In some cases, the user is forced to come back to home page. When this happens, the state contains traveller details data
   * On quote stage initial, the get quote will fail because it contains more information than the stage allows to and it chains
   * a number of issues because of that. This action will be called whenever the home page is re-loaded.
   */
  export class ResetPolicyHolders {
    public static readonly type = '[Quote] Reset policy holders';
  }

  /**
   * In some cases, the user is forced to come back to home page. When this happens, the state contains traveller details data
   * On quote stage initial, the get quote will fail because it contains more information than the stage allows to and it chains
   * a number of issues because of that. This action will be called whenever the home page is re-loaded.
   */
  export class ResetHomeAddress {
    public static readonly type = '[Quote] Reset address';
  }

  /**Action to reset options when planId changes */
  export class ResetOptions {
    public static readonly type = '[Quote] Reset Options';
  }

  export class ResetPlanPrices {
    public static readonly type = '[Quote] Reset Base Prices';
    constructor(public planPrices: IQuotePriceSummary[] | undefined) {}
  }

  /** Action to apply the default promo code from brand during init */
  export class SetDefaultCampaignCode {
    public static readonly type = '[Quote] Set Brand default campaign code';

    constructor(public defaultCode: string | undefined) {}
  }

  /**Action to update partner code */
  export class SetPartnerCode {
    public static readonly type = '[Quote] Set partner code';

    constructor(public partnerCode: string | undefined) {}
  }

  /**Action to set duplicate Purchase flag */
  export class ContinueDuplicatePurchase {
    public static readonly type = '[Quote] Continue duplicate purchase';
  }

  /**Action to email quote to specified email address */
  export class SendEmailQuote {
    public static readonly type = '[Quote] Send Email';

    constructor(public emailAddress: string) {}
  }

  export class SetFootprintsData {
    public static readonly type = '[Footprints] Set Footprints Data';
    constructor(
      public partnerCode: string,
      public projectId: number,
      public amount: number
    ) {}
  }

  export class ResetFootprints {
    public static readonly type = '[Footprints] Reset footprints';
  }

  export class LoadQuotePlans {
    public static readonly type = '[Quote] Load Quote plans';
  }

  export class NavigateQuoteStart {
    public static readonly type = '[Quote] Navigate quote start';
  }

  export class InvalidQuote {
    public static readonly type = '[Quote] Invalid quote';

    constructor(public validation: IValidation) {}
  }

  export class QuoteNotFound {
    public static readonly type = '[Quote] Not found';
  }

  export class UpdatePolicyHolders {
    public static readonly type = '[Quote] Update Policyholders';

    constructor(public policyHolders: IPolicyHoldersInput[]) {}
  }

  export class UpdateAddress {
    public static readonly type = '[Quote] Update Address';

    constructor(public address: IAddressInput) {}
  }

  export class UpdateRegulatoryDeclarations {
    public static readonly type = '[Quote] Update Regulatory Declarations';

    constructor(public regulatoryDeclarations: IRegulatoryDeclarationInput[]) {}
  }

  export class SetDefaultCancellationOption {
    public static readonly type = '[Quote] Set default cancellation option';
  }

  export class SetAnalyticParams {
    public static readonly type = '[Quote] Set analytics params';
    constructor(public params: Record<string, string> | undefined) {}
  }

  export class EditTripModal {
    public static readonly type = '[Quote] Edit Trip Modal Open';
  }

  export class TogglePlanDetailsAccordion {
    public static readonly type = '[Quote] Toggle plan details accordion';
    constructor(public data: { accordionOpen: boolean; title: string }) {}
  }
}
